import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Zap, CheckCircle2, Send, Camera } from "lucide-react";
import { business, waLink } from "../config/business";
import { useLanguage } from "../i18n/LanguageContext";

const HERO_IMG = "/images/hero-car.webp";
const HERO_IMG_MOBILE = "/images/hero-car-mobile.webp";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0.2]);

  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative isolate flex min-h-[88svh] items-center overflow-hidden pt-16"
    >
      {/* Fondo con parallax */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <picture>
          <source media="(max-width: 767px)" srcSet={HERO_IMG_MOBILE} />
          <img
            src={HERO_IMG}
            alt="UNLockSmiths branded locksmith car parked on an Edinburgh street"
            loading="eager"
            fetchPriority="high"
            className="h-[120%] w-full object-cover object-[60%_30%] md:object-[center_45%]"
          />
        </picture>
        {/* Texto a la izquierda: oscurecemos la izquierda y dejamos ver al cerrajero a la derecha */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1526]/95 via-[#0A1526]/75 to-[#0A1526]/35 md:from-[#0A1526] md:via-[#0A1526]/70 md:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1526]/40 via-[#0A1526]/55 to-[#0A1526] md:from-[#0A1526]/70 md:via-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,21,38,0.45)_100%)]" />
        {/* Brillos ambientales: dorado y azul del logo */}
        <div className="absolute -top-32 right-[-10%] h-96 w-96 rounded-full bg-yellow-400/10 blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-8%] h-[28rem] w-[28rem] rounded-full bg-blue-600/20 blur-3xl" />
      </motion.div>

      <motion.div style={{ opacity: contentOpacity }} className="mx-auto w-full max-w-6xl px-5 sm:px-8 py-16 sm:py-20">
        <motion.div
          data-testid="hero-eta-badge"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="animate-live-glow mb-10 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 font-mono text-xs font-semibold text-emerald-400 backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
        >
          <Zap className="h-3.5 w-3.5" strokeWidth={2.5} />
          {t.hero.badge}
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[1.02] tracking-[-0.035em] drop-shadow-[0_8px_30px_rgba(0,0,0,0.45)]"
        >
          {lines.map((line, i) => (
            <MaskedLine key={line} delay={0.25 + i * 0.14}>
              {i === 1 ? (
                <span className="bg-gradient-to-r from-yellow-200 via-amber-400 to-yellow-500 bg-clip-text text-transparent drop-shadow-[0_0_28px_rgba(234,179,8,0.35)]">
                  {line}
                </span>
              ) : (
                line
              )}
            </MaskedLine>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl space-y-3"
        >
          <p data-testid="hero-sub-1" className="text-base sm:text-lg font-semibold leading-relaxed text-slate-100">{t.hero.sub1}</p>
          <p data-testid="hero-sub-2" className="text-base sm:text-lg leading-relaxed text-slate-300">{t.hero.sub2}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:gap-5"
        >
          <a
            data-testid="hero-whatsapp-cta"
            href={waLink(t.hero.waMessage)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-wa rounded-2xl px-8 py-4 text-lg"
          >
            <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
            {t.hero.whatsapp}
          </a>
          <a
            data-testid="hero-message-cta"
            href="#contact"
            className="btn btn-sky rounded-2xl px-8 py-4 text-lg"
          >
            <Send className="h-5 w-5" strokeWidth={2.5} />
            {t.hero.quote}
          </a>
        </motion.div>

        <motion.div
          data-testid="hero-trust-indicators"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-12 flex flex-wrap gap-3"
        >
          {t.hero.trust.map((item) => (
            <span
              key={item}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-slate-300 backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition-colors duration-300 hover:border-emerald-400/30 hover:bg-white/[0.07]"
            >
              <CheckCircle2 className="h-4 w-4 text-emerald-400" strokeWidth={2.2} />
              {item}
            </span>
          ))}
        </motion.div>

        <motion.p
          data-testid="hero-price-line"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-6 inline-flex items-center gap-2.5 rounded-2xl border border-amber-400/25 bg-amber-400/10 px-4 py-3 text-sm sm:text-base font-semibold text-amber-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
        >
          <Camera className="h-4 w-4 shrink-0 text-amber-400" strokeWidth={2.5} />
          {t.hero.priceLine}
        </motion.p>
      </motion.div>
    </section>
  );
};

export default Hero;
